import { JSX, useCallback, useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ImageSlideshow from "lib/components/image/ImageSlideshow";
import ImageView from "lib/components/image/ImageView";

import { ChangeEvent } from "utils/history/Event";
import { compress } from "utils/ImageUtils";

import {
	InformationRight,
	InformationRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import {
	createResource,
	getResourceContent,
	updateResourceContent
} from "resource/ResourceService";

import { ImageField } from "../FieldService";

export type ImageInputProps = {
	field: ImageField;
	editable: boolean;
	rights: InformationRights;

	onChange: (event: ChangeEvent, valid: boolean) => void;
};

export default function ImageInput(
	props: Readonly<ImageInputProps>
): JSX.Element {

	const [imageUrl, setImageUrl] = useState<string>();
	const [uploading, setUploading] = useState(false);
	const [slideshow, setSlideshow] = useState(false);

	useEffect(() => {
		if (props.field.value.length > 0) {
			getResourceContent(props.field.value[0]).then(updateImage);
		}
	}, [props.field.value]);

	useEffect(() => {
		return () => {
			if (imageUrl) {
				URL.revokeObjectURL(imageUrl);
			}
		};
	}, [imageUrl]);

	const updateImage = useCallback((blob?: Blob) => {
		setImageUrl(current => {
			current && URL.revokeObjectURL(current);
			return blob ? URL.createObjectURL(blob) : undefined;
		});
	}, []);

	async function uploadImage(image: Blob): Promise<void> {
		setUploading(true);
		const file = await compress(image);

		const data = new FormData();
		data.append("file", file);
		createResource({
			name: file.name,
			mediaType: file.type,
			userRights: []
		})
			.then(location => {
				const id = location.substring(location.lastIndexOf("/") + 1);
				return Number.parseInt(id, 10);
			})
			.then(id => {
				updateResourceContent(id, data).then(() => {
					const newValue = [id];
					const change = new ChangeEvent(
						"/value",
						props.field.value,
						newValue
					);
					props.onChange(change, true);
					setUploading(false);
				});
			})
			.catch(() => {
				setUploading(false);
				deleteImage();
				throw new Error("failed to create resource");
			});
	}

	const deleteImage = useCallback((): void => {
		updateImage();
		const change = new ChangeEvent("/value", props.field.value, []);
		props.onChange(change, !props.field.required);
	}, [props.onChange]);

	const onSelectFile = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			if (!event.target.files || event.target.files.length === 0) {
				deleteImage();
				return;
			}

			const f = event.target.files[0];
			if (f) {
				uploadImage(f);
				updateImage(f);
			}
		},
		[props.onChange, props.field.value]
	);

	const openSlideShow = useCallback(() => setSlideshow(true), []);
	const closeSlideshow = useCallback(() => setSlideshow(false), []);

	function isAllowedToEdit(): boolean {
		return (
			props.editable &&
			props.rights[props.field.uuid][InformationRight.WRITE]
		);
	}

	return (
		<Grid
			item
			container
			xs={12}
			md={6}
			columnSpacing={2}
			alignItems="center">
			<Grid item xs={12} sm={6}>
				<Typography variant="subtitle2" sx={{ wordWrap: "break-word" }}>
					{props.field.name}
					{props.field.required && " *"}
				</Typography>
			</Grid>
			<Grid item xs={12} sm={6}>
				{isAllowedToEdit() && (
					<TextField
						type="file"
						variant="standard"
						inputProps={{ accept: ".jpg,.jpeg,.png" }}
						onChange={onSelectFile}
					/>
				)}
				{imageUrl && (
					<ImageView
						url={imageUrl}
						loading={uploading}
						onClick={openSlideShow}
					/>
				)}
				{slideshow && imageUrl && (
					<ImageSlideshow
						images={[imageUrl]}
						onClose={closeSlideshow}
					/>
				)}
			</Grid>
		</Grid>
	);
}
